import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, gql } from '@apollo/client'
import { Accordion, Button, Card, Modal, Spinner, Table } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { defineMessages, useIntl } from 'react-intl'
// import axios from 'axios'

import { GET_BOTMAKER_WHATSAPP_TEMPLATES } from '../../gql/queries/botmaker'
import { SYNC_BOTMAKER_TEMPLATES, TOGGLE_BOTMAKER_TEMPLATE_ACTIVE, ARCHIVE_BOTMAKER_TEMPLATE, DELETE_BOTMAKER_WHATSAPP_TEMPLATE } from '../../gql/mutations/botmaker'
import { WhatsAppPreview } from '../templates/components/tiptap/WhatsAppPreview'
import { CreateBotmakerTemplateModal } from './CreateBotmakerTemplateModal'
import { TemplateConfigurationModal } from './TemplateConfigurationModal'
import { useChannelConfig } from '../channelConfig/hooks/useChannelConfig'
import { TwilioTemplatesView } from './TwilioTemplatesView'

const M = defineMessages({
  // Modal de registros
  logsTitle: {id: 'integrations.whatsapp.logs.title', defaultMessage: 'Message logs (last 50)'},
  logsEmpty: {id: 'integrations.whatsapp.logs.empty', defaultMessage: 'No logs found.'},
  logsColTemplate: {id: 'integrations.whatsapp.logs.col.template', defaultMessage: 'Template'},
  logsColModule: {id: 'integrations.whatsapp.logs.col.module', defaultMessage: 'Module'},
  logsColPayload: {id: 'integrations.whatsapp.logs.col.payload', defaultMessage: 'Payload'},

  // Cabecera de la pagina
  title: {id: 'integrations.whatsapp.title', defaultMessage: 'WhatsApp Templates (Botmaker)'},
  subtitle: {
    id: 'integrations.whatsapp.subtitle',
    defaultMessage: 'List, filter and create WhatsApp templates using your Botmaker account.',
  },
  logs: {id: 'integrations.whatsapp.action.logs', defaultMessage: 'Logs'},
  create: {id: 'integrations.template.create', defaultMessage: 'Create template'},

  // Tabla
  colMetaStatus: {id: 'integrations.whatsapp.col.metaStatus', defaultMessage: 'Meta status'},
  colEnabled: {id: 'integrations.whatsapp.col.enabled', defaultMessage: 'Enabled'},
  colLocale: {id: 'integrations.template.locale', defaultMessage: 'Locale'},
  colCategory: {id: 'integrations.template.category', defaultMessage: 'Category'},
  colUpdated: {id: 'integrations.whatsapp.col.updated', defaultMessage: 'Updated'},
  empty: {id: 'integrations.whatsapp.empty', defaultMessage: 'No templates found.'},
  badgeArchivedUpper: {
    id: 'integrations.whatsapp.badge.archivedUpper',
    defaultMessage: 'ARCHIVED',
  },
  tooltipDeactivate: {
    id: 'integrations.template.deactivateTitle',
    defaultMessage: 'Deactivate template',
  },
  tooltipActivate: {
    id: 'integrations.template.activateTitle',
    defaultMessage: 'Activate template',
  },
  tooltipArchive: {
    id: 'integrations.whatsapp.tooltip.archive',
    defaultMessage: 'Archive template (for templates deleted from Meta)',
  },
  tooltipDelete: {
    id: 'integrations.whatsapp.tooltip.delete',
    defaultMessage: 'Delete template from database',
  },

  // Modal de detalle
  detailTitle: {id: 'integrations.whatsapp.detail.title', defaultMessage: 'Template details'},
  detailLocale: {id: 'integrations.whatsapp.detail.locale', defaultMessage: 'Locale: {value}'},
  detailBot: {id: 'integrations.whatsapp.detail.bot', defaultMessage: 'Bot: {value}'},
  detailLines: {id: 'integrations.whatsapp.detail.lines', defaultMessage: 'Lines: {value}'},
  detailRequester: {
    id: 'integrations.whatsapp.detail.requester',
    defaultMessage: 'Requester: {value}',
  },
  detailOptInImage: {
    id: 'integrations.whatsapp.detail.optInImage',
    defaultMessage: 'Opt-in image:',
  },
  detailPreview: {id: 'integrations.whatsapp.detail.preview', defaultMessage: 'Preview WhatsApp'},
  detailContent: {
    id: 'integrations.whatsapp.detail.content',
    defaultMessage: 'Content (text & footer)',
  },
  detailNoBody: {
    id: 'integrations.whatsapp.detail.noBody',
    defaultMessage: 'No message body to preview.',
  },
  detailBodyText: {id: 'integrations.template.bodyText', defaultMessage: 'Body text'},

  // Confirmaciones (window.confirm — interfaz aunque no sea JSX)
  confirmActivate: {
    id: 'integrations.whatsapp.confirm.activate',
    defaultMessage: 'Are you sure you want to activate the template "{name}"?',
  },
  confirmDeactivate: {
    id: 'integrations.whatsapp.confirm.deactivate',
    defaultMessage: 'Are you sure you want to deactivate the template "{name}"?',
  },
  confirmArchive: {
    id: 'integrations.whatsapp.confirm.archive',
    defaultMessage:
      'Are you sure you want to ARCHIVE the template "{name}"?\n\nArchived templates cannot be used anymore (usually because they were deleted from Meta).',
  },
  confirmDelete: {
    id: 'integrations.whatsapp.confirm.delete',
    defaultMessage:
      'WARNING! Are you sure you want to PERMANENTLY DELETE the template "{name}" from the local database?\n\nThis action cannot be undone.',
  },

  // Toasts
  toastLoadError: {
    id: 'integrations.whatsapp.toast.loadError',
    defaultMessage: 'Error loading templates',
  },
  toastSynced: {
    id: 'integrations.whatsapp.toast.synced',
    defaultMessage: '{count, plural, one {Synced # template} other {Synced # templates}}',
  },
  toastSyncFailed: {
    id: 'integrations.whatsapp.toast.syncFailed',
    defaultMessage: 'Sync failed',
  },
  toastToggleFailed: {
    id: 'integrations.whatsapp.toast.toggleFailed',
    defaultMessage: 'Failed to toggle template status',
  },
  toastArchiveFailed: {
    id: 'integrations.template.toast.archiveFailed',
    defaultMessage: 'Failed to archive template',
  },
  toastDeleteFailed: {
    id: 'integrations.template.toast.deleteFailed',
    defaultMessage: 'Failed to delete template',
  },

  // Proveedor deshabilitado
  disabledTitle: {
    id: 'integrations.whatsapp.disabled.title',
    defaultMessage: 'WhatsApp provider disabled',
  },
  disabledHint: {
    id: 'integrations.whatsapp.disabled.hint',
    defaultMessage:
      "To manage WhatsApp templates, configure Botmaker or Twilio in the organization's channel configuration.",
  },

  // Reusadas
  date: {id: 'common.field.date', defaultMessage: 'Date'},
  to: {id: 'common.field.to', defaultMessage: 'To'},
  user: {id: 'common.field.user', defaultMessage: 'User'},
  providerId: {id: 'comms.campaigns.detail.colProviderId', defaultMessage: 'Provider ID'},
  actions: {id: 'common.field.actions', defaultMessage: 'Actions'},
  name: {id: 'common.field.name', defaultMessage: 'Name'},
  templates: {id: 'MENU.item.templates', defaultMessage: 'Templates'},
  archived: {id: 'comms.campaigns.templates.archivedBadge', defaultMessage: 'Archived'},
  yes: {id: 'common.boolean.yes', defaultMessage: 'Yes'},
  no: {id: 'common.boolean.no', defaultMessage: 'No'},
  back: {id: 'common.action.back', defaultMessage: 'Back'},
  refresh: {id: 'common.action.refresh', defaultMessage: 'Refresh'},
  sync: {id: 'telephony.groups.sync', defaultMessage: 'Sync'},
  view: {id: 'common.action.view', defaultMessage: 'View'},
  footer: {id: 'comms.templates.variant.sectionFooter', defaultMessage: 'Footer'},
})

// El guion largo es puntuacion de relleno, no texto (§17.3).
const DASH = '—'
// i18n-ok: estado que devuelve la API de Meta cuando no lo conocemos.
const UNKNOWN_STATE = 'UNKNOWN'

const MessageLogsModal = ({ show, onHide }: { show: boolean; onHide: () => void }) => {
  const intl = useIntl()
  const { data, loading, refetch } = useQuery(gql`
        query GetBotmakerMessageLogs {
            botmakerMessageLogs(limit: 50) {
            id
            createdAt
            toPhone
            templateName
            module
            botmakerId
            user
            payload
            }
        }
    `, {
    fetchPolicy: 'network-only',
    skip: !show
  })

  const logs = data?.botmakerMessageLogs || []

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>{intl.formatMessage(M.logsTitle)}</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        <div className="d-flex justify-content-end mb-3">
          <Button variant="outline-primary" size="sm" onClick={() => refetch()} disabled={loading}>
            <i className="bi bi-arrow-repeat me-1" /> {intl.formatMessage(M.refresh)}
          </Button>
        </div>
        {loading && logs.length === 0 ? (
          <div className="text-center py-5"><Spinner animation="border" /></div>
        ) : logs.length === 0 ? (
          <div className="text-center py-5 text-muted">{intl.formatMessage(M.logsEmpty)}</div>
        ) : (
          <Table bordered hover size="sm" className="small">
            <thead className="table-light">
              <tr>
                <th>{intl.formatMessage(M.date)}</th>
                <th>{intl.formatMessage(M.to)}</th>
                <th>{intl.formatMessage(M.logsColTemplate)}</th>
                <th>{intl.formatMessage(M.logsColModule)}</th>
                <th>{intl.formatMessage(M.user)}</th>
                <th>{intl.formatMessage(M.providerId)}</th>
                <th>{intl.formatMessage(M.actions)}</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log: any) => (
                <tr key={log.id}>
                  <td>{new Date(log.createdAt).toLocaleString()}</td>
                  <td>{log.toPhone}</td>
                  <td>{log.templateName}</td>
                  <td>{log.module}</td>
                  <td>{log.user}</td>
                  <td>
                    <code>{log.botmakerId}</code>
                  </td>
                  <td>
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0"
                      onClick={() => {
                        // Simple alert for now, could be a nested modal
                        alert(JSON.stringify(log.payload, null, 2))
                      }}
                    >
                      {intl.formatMessage(M.logsColPayload)}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Modal.Body>
    </Modal>
  )
}

type TemplateState = 'APPROVED' | 'REJECTED' | 'PENDING' | string

type WhatsAppTemplate = {
  id?: string
  name?: string
  locale?: string
  category?: string
  status?: TemplateState
  state?: TemplateState
  createdAt?: string
  updatedAt?: string
  botName?: string
  phoneLinesNumbers?: string[]
  optInImage?: string
  requesterEmail?: string
  body?: { text?: string }
  footer?: { text?: string }
  allowedModules?: string[]
  variableMapping?: Record<string, string>
  isActive?: boolean
  isArchived?: boolean
}


const BotmakerTemplatesView = () => {
  const intl = useIntl()
  const navigate = useNavigate()
  const [filterState, setFilterState] = useState<string>('')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showConfigModal, setShowConfigModal] = useState(false)
  const [selected, setSelected] = useState<WhatsAppTemplate | null>(null)
  const [showLogsModal, setShowLogsModal] = useState(false)
  const [configTemplate, setConfigTemplate] = useState<WhatsAppTemplate | null>(null)
  const [syncTemplates, { loading: syncing }] = useMutation(SYNC_BOTMAKER_TEMPLATES)
  const [toggleTemplateActive] = useMutation(TOGGLE_BOTMAKER_TEMPLATE_ACTIVE)
  const [archiveTemplate] = useMutation(ARCHIVE_BOTMAKER_TEMPLATE)
  const [deleteTemplate, { loading: deleting }] = useMutation(DELETE_BOTMAKER_WHATSAPP_TEMPLATE)

  const { data, loading, refetch } = useQuery(GET_BOTMAKER_WHATSAPP_TEMPLATES, {
    variables: { state: filterState || null },
    fetchPolicy: 'cache-and-network', // Ensure we always verify with server
    onCompleted: (d) => {
      console.log('Templates data updated:', d?.botmakerWhatsappTemplates?.length, 'items')
    },
    notifyOnNetworkStatusChange: true,
    onError: (err) => toast.error(err.message || intl.formatMessage(M.toastLoadError)),
  })

  const templatesData = useMemo(() => data?.botmakerWhatsappTemplates || [], [data])

  useEffect(() => {
    refetch({ state: filterState || null })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterState])

  const handleSync = async () => {
    try {
      const { data } = await syncTemplates()
      if (data?.syncBotmakerTemplates?.success) {
        toast.success(
          intl.formatMessage(M.toastSynced, {count: data.syncBotmakerTemplates.count})
        )
        refetch()
      } else {
        toast.error(data?.syncBotmakerTemplates?.message || intl.formatMessage(M.toastSyncFailed))
      }
    } catch (error: any) {
      console.error(error)
      toast.error(error.message || intl.formatMessage(M.toastSyncFailed))
    }
  }

  const handleToggleActive = async (template: WhatsAppTemplate) => {
    const newStatus = !template.isActive
    const confirmMessage = intl.formatMessage(
      newStatus ? M.confirmActivate : M.confirmDeactivate,
      {name: template.name}
    )

    if (!window.confirm(confirmMessage)) {
      return
    }

    try {
      const { data } = await toggleTemplateActive({
        variables: {
          botmakerId: template.id,
          isActive: newStatus
        },
        refetchQueries: ['BotmakerWhatsappTemplates'],
        awaitRefetchQueries: true
      })

      if (data?.toggleBotmakerTemplateActive?.success) {
        toast.success(data.toggleBotmakerTemplateActive.message)
        // Force a refetch to ensure UI updates
        await refetch()
      } else {
        toast.error(intl.formatMessage(M.toastToggleFailed))
      }
    } catch (error: any) {
      console.error(error)
      toast.error(error.message || intl.formatMessage(M.toastToggleFailed))
    }
  }

  const handleArchive = async (template: WhatsAppTemplate) => {
    if (!window.confirm(intl.formatMessage(M.confirmArchive, {name: template.name}))) {
      return
    }

    try {
      const { data } = await archiveTemplate({
        variables: {
          botmakerId: template.id
        },
        refetchQueries: ['BotmakerWhatsappTemplates'],
        awaitRefetchQueries: true
      })

      if (data?.archiveBotmakerTemplate?.success) {
        toast.success(data.archiveBotmakerTemplate.message)
        // Force a refetch to ensure UI updates
        await refetch()
      } else {
        toast.error(intl.formatMessage(M.toastArchiveFailed))
      }
    } catch (error: any) {
      console.error(error)
      toast.error(error.message || intl.formatMessage(M.toastArchiveFailed))
    }
  }

  const handleDelete = async (template: WhatsAppTemplate) => {
    if (!window.confirm(intl.formatMessage(M.confirmDelete, {name: template.name}))) {
      return
    }

    try {
      const { data } = await deleteTemplate({
        variables: {
          botmakerId: template.id
        },
        refetchQueries: ['BotmakerWhatsappTemplates'],
        awaitRefetchQueries: true
      })

      if (data?.deleteBotmakerWhatsappTemplate?.success) {
        toast.success(data.deleteBotmakerWhatsappTemplate.message)
        await refetch()
      } else {
        toast.error(intl.formatMessage(M.toastDeleteFailed))
      }
    } catch (error: any) {
      console.error(error)
      toast.error(error.message || intl.formatMessage(M.toastDeleteFailed))
    }
  }

  return (
    <div className='container-fluid'>
      <div className='d-flex align-items-center justify-content-between mb-4'>
        <div>
          <h3>{intl.formatMessage(M.title)}</h3>
          <p className='text-muted mb-0'>{intl.formatMessage(M.subtitle)}</p>
        </div>
        <div className='d-flex gap-2'>
          <Button variant='outline-secondary' onClick={() => navigate(-1)}>
            <i className='bi bi-arrow-left-short me-1' />
            {intl.formatMessage(M.back)}
          </Button>
          <Button
            variant='outline-primary'
            onClick={() => refetch({ state: filterState || null })}
            disabled={loading}
          >
            <i className='bi bi-arrow-repeat me-1' />
            {intl.formatMessage(M.refresh)}
          </Button>
          <Button
            variant='outline-secondary'
            onClick={() => setShowLogsModal(true)}
          >
            <i className='bi bi-list-ul me-1' />
            {intl.formatMessage(M.logs)}
          </Button>
          <Button
            variant='info'
            onClick={handleSync}
            disabled={syncing || loading}
            className='text-white'
          >
            {syncing ? <Spinner animation='border' size='sm' className='me-1' /> : <i className='bi bi-cloud-arrow-down-fill me-1' />}
            {intl.formatMessage(M.sync)}
          </Button>
          <Button variant='primary' onClick={() => setShowCreateModal(true)}>
            <i className='bi bi-plus-lg me-1' />
            {intl.formatMessage(M.create)}
          </Button>
        </div>
      </div>

      <Card>
        <Card.Header>{intl.formatMessage(M.templates)}</Card.Header>
        <Card.Body>
          {loading ? (
            <div className='d-flex justify-content-center py-4'>
              <Spinner animation='border' />
            </div>
          ) : (
            <Table responsive hover>
              <thead>
                <tr>
                  <th>{intl.formatMessage(M.name)}</th>
                  <th>{intl.formatMessage(M.colMetaStatus)}</th>
                  <th>{intl.formatMessage(M.colEnabled)}</th>
                  <th>{intl.formatMessage(M.colLocale)}</th>
                  <th>{intl.formatMessage(M.colCategory)}</th>
                  <th>{intl.formatMessage(M.colUpdated)}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {templatesData.length === 0 && (
                  <tr>
                    <td colSpan={8} className='text-center text-muted py-3'>
                      {intl.formatMessage(M.empty)}
                    </td>
                  </tr>
                )}
                {templatesData.map((tpl) => {
                  const isArchived = tpl.isArchived === true
                  const isActive = tpl.isActive !== false && !isArchived

                  return (
                    <tr
                      key={tpl.id || tpl.name}
                      style={{
                        opacity: isArchived ? 0.4 : (isActive ? 1 : 0.65),
                        backgroundColor: isArchived ? '#f5f5f5' : (isActive ? 'transparent' : '#f8f9fa')
                      }}
                    >
                      <td>
                        {tpl.name || DASH}
                        {isArchived && (
                          <span className='badge bg-dark ms-2' style={{ fontSize: '0.7rem' }}>
                            {intl.formatMessage(M.badgeArchivedUpper)}
                          </span>
                        )}
                      </td>
                      <td>
                        <span className='badge bg-light text-dark'>
                          {tpl.status || tpl.state || UNKNOWN_STATE}
                        </span>
                      </td>
                      <td>
                        {isArchived ? (
                          <span className='badge bg-dark'>{intl.formatMessage(M.archived)}</span>
                        ) : isActive ? (
                          <span className='badge bg-success'>{intl.formatMessage(M.yes)}</span>
                        ) : (
                          <span className='badge bg-secondary'>{intl.formatMessage(M.no)}</span>
                        )}
                      </td>
                      <td>{tpl.locale || DASH}</td>
                      <td>{tpl.category || DASH}</td>
                      <td>
                        {tpl.updatedAt
                          ? new Date(tpl.updatedAt).toLocaleString()
                          : tpl.createdAt
                            ? new Date(tpl.createdAt).toLocaleString()
                            : DASH}
                      </td>
                      <td className='text-end'>
                        <Button
                          variant='outline-secondary'
                          size='sm'
                          className='me-2'
                          onClick={() => {
                            const parsed = { ...tpl }
                            try {
                              if (typeof tpl.body === 'string') {
                                parsed.body = JSON.parse(tpl.body)
                              }
                              if (typeof tpl.footer === 'string') {
                                parsed.footer = JSON.parse(tpl.footer)
                              }
                              if (typeof tpl.variableMapping === 'string') {
                                parsed.variableMapping = JSON.parse(tpl.variableMapping)
                              }
                            } catch (e) {
                              console.error('Error parsing template content', e)
                            }
                            setSelected(parsed)
                          }}
                        >
                          <i className='bi bi-eye' />
                        </Button>
                        <Button
                          variant='outline-primary'
                          size='sm'
                          onClick={() => {
                            let parsedMapping = {}
                            try {
                              if (typeof tpl.variableMapping === 'string') {
                                parsedMapping = JSON.parse(tpl.variableMapping)
                              } else {
                                parsedMapping = tpl.variableMapping || {}
                              }
                            } catch (e) { }

                            setConfigTemplate({
                              ...tpl,
                              variableMapping: parsedMapping
                            })
                            setShowConfigModal(true)
                          }}
                        >
                          <i className='bi bi-gear' />
                        </Button>
                        <Button
                          variant={isActive ? 'outline-danger' : 'outline-success'}
                          size='sm'
                          className='ms-2'
                          onClick={() => handleToggleActive(tpl)}
                          title={intl.formatMessage(
                            isActive ? M.tooltipDeactivate : M.tooltipActivate
                          )}
                          disabled={isArchived}
                        >
                          <i className={isActive ? 'bi bi-toggle-on' : 'bi bi-toggle-off'} />
                        </Button>
                        {!isArchived && (
                          <Button
                            variant='outline-warning'
                            size='sm'
                            className='ms-2'
                            onClick={() => handleArchive(tpl)}
                            title={intl.formatMessage(M.tooltipArchive)}
                          >
                            <i className='bi bi-archive' />
                          </Button>
                        )}
                        <Button
                          variant='outline-danger'
                          size='sm'
                          className='ms-2'
                          onClick={() => handleDelete(tpl)}
                          title={intl.formatMessage(M.tooltipDelete)}
                          disabled={deleting}
                        >
                          <i className='bi bi-trash' />
                        </Button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Modal show={Boolean(selected)} onHide={() => setSelected(null)} size='lg' centered>
        <Modal.Header closeButton>
          <Modal.Title>{intl.formatMessage(M.detailTitle)}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selected && (
            <div className='d-flex flex-column gap-3'>
              <div className='d-flex justify-content-between'>
                <div>
                  <div className='fw-bold'>{selected.name || DASH}</div>
                  <div className='text-muted'>{selected.category || DASH}</div>
                </div>
                <div className='text-end'>
                  <div className='badge bg-light text-dark'>
                    {selected.status || selected.state || UNKNOWN_STATE}
                  </div>
                  <div className='small text-muted'>
                    {selected.updatedAt
                      ? new Date(selected.updatedAt).toLocaleString()
                      : selected.createdAt
                        ? new Date(selected.createdAt).toLocaleString()
                        : ''}
                  </div>
                </div>
              </div>
              <div className='small text-muted'>
                {intl.formatMessage(M.detailLocale, {value: selected.locale || DASH})}
              </div>
              {selected.botName && (
                <div className='small'>
                  {intl.formatMessage(M.detailBot, {value: selected.botName})}
                </div>
              )}
              {selected.phoneLinesNumbers && selected.phoneLinesNumbers.length > 0 && (
                <div className='small'>
                  {intl.formatMessage(M.detailLines, {
                    value: selected.phoneLinesNumbers.join(', '),
                  })}
                </div>
              )}
              {selected.requesterEmail && (
                <div className='small'>
                  {intl.formatMessage(M.detailRequester, {value: selected.requesterEmail})}
                </div>
              )}
              {selected.optInImage && (
                <div className='small'>
                  <span className='me-1'>{intl.formatMessage(M.detailOptInImage)}</span>
                  <a href={selected.optInImage} target='_blank' rel='noreferrer'>
                    {intl.formatMessage(M.view)}
                  </a>
                </div>
              )}

              <Accordion defaultActiveKey='0' className='mb-3'>
                <Accordion.Item eventKey='0'>
                  <Accordion.Header>{intl.formatMessage(M.detailPreview)}</Accordion.Header>
                  <Accordion.Body>
                    {selected.body?.text ? (
                      <WhatsAppPreview
                        message={selected.body?.text || ''}
                        header={undefined}
                        footer={selected.footer?.text}
                      />
                    ) : (
                      <div className='text-muted'>{intl.formatMessage(M.detailNoBody)}</div>
                    )}
                  </Accordion.Body>
                </Accordion.Item>
                <Accordion.Item eventKey='1'>
                  <Accordion.Header>{intl.formatMessage(M.detailContent)}</Accordion.Header>
                  <Accordion.Body>
                    <h6 className='fw-bold mb-2'>{intl.formatMessage(M.detailBodyText)}</h6>
                    <pre className='mb-3 bg-light p-2 rounded' style={{ whiteSpace: 'pre-wrap' }}>
                      {selected.body?.text || DASH}
                    </pre>

                    {selected.footer?.text && (
                      <>
                        <h6 className='fw-bold mb-2'>{intl.formatMessage(M.footer)}</h6>
                        <pre
                          className='mb-0 bg-light p-2 rounded'
                          style={{ whiteSpace: 'pre-wrap' }}
                        >
                          {selected.footer.text}
                        </pre>
                      </>
                    )}
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>
            </div>
          )}
        </Modal.Body>
      </Modal>

      <CreateBotmakerTemplateModal
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
        onSuccess={() => refetch({ state: filterState || null })}
      />

      <MessageLogsModal show={showLogsModal} onHide={() => setShowLogsModal(false)} />

      {configTemplate && (
        <TemplateConfigurationModal
          show={showConfigModal}
          onHide={() => {
            setShowConfigModal(false)
            setConfigTemplate(null)
          }}
          template={configTemplate}
          onSuccess={() => refetch()}
        />
      )}

      {loading && (
        <div
          className='d-flex flex-center'
          style={{
            inset: 0,
            width: '100%',
            height: '100%',
            backgroundColor: '#000',
            color: 'white',
            opacity: 0.35,
            position: 'fixed',
            zIndex: 10000000,
          }}
        >
          <div>
            <Spinner animation='border' style={{ height: '3em', width: '3em' }} />
          </div>
        </div>
      )}
    </div>
  )
}

export const WhatsAppTemplatesPage = () => {
  const intl = useIntl()
  const { config, loading } = useChannelConfig()

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-10">
        <Spinner animation="border" />
      </div>
    )
  }

  if (config.whatsapp_provider === 'TWILIO') {
    return <TwilioTemplatesView />
  }

  if (config.whatsapp_provider === 'BOTMAKER') {
    return <BotmakerTemplatesView />
  }

  return (
    <div className="card">
      <div className="card-body py-10 text-center">
        <h3 className="text-muted">{intl.formatMessage(M.disabledTitle)}</h3>
        <p className="mt-3">{intl.formatMessage(M.disabledHint)}</p>
      </div>
    </div>
  )
}

export default WhatsAppTemplatesPage
